import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { User } from '@supabase/supabase-js';
import { ArrowLeft } from 'lucide-react';
import { z } from 'zod';
import { BatchEmbeddingProcessor } from '@/components/BatchEmbeddingProcessor';
import { EmptyAllPhotosDialog } from '@/components/EmptyAllPhotosDialog';
import { emptyAllPhotos } from '@/utils/photoUpload';

const passwordSchema = z.string()
  .min(12, 'Password must be at least 12 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showEmptyAllDialog, setShowEmptyAllDialog] = useState(false);
  const [isEmptyingAll, setIsEmptyingAll] = useState(false);
  const [allPhotoCount, setAllPhotoCount] = useState(0);

  useEffect(() => {
    // Check if user is logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/auth');
      } else {
        setUser(session.user);
        // Get organization ID
        supabase
          .from('user_organizations')
          .select('organization_id')
          .eq('user_id', session.user.id)
          .single()
          .then(({ data }) => {
            if (data) setOrganizationId(data.organization_id);
          });
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate('/auth');
      } else {
        setUser(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate new password
      passwordSchema.parse(newPassword);

      // Check if passwords match
      if (newPassword !== confirmPassword) {
        toast.error('New passwords do not match');
        setIsLoading(false);
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPhotoCount = async () => {
    if (!organizationId) return;
    const { count } = await supabase
      .from('photos')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId);
    setAllPhotoCount(count || 0);
  };

  const handleEmptyAllPhotos = async () => {
    if (!organizationId) return;
    setIsEmptyingAll(true);
    const result = await emptyAllPhotos(organizationId);
    if (result.success) {
      toast.success(`Deleted ${result.deletedCount} photo${result.deletedCount !== 1 ? 's' : ''}`);
      setShowEmptyAllDialog(false);
      setAllPhotoCount(0);
    } else {
      toast.error(result.error || 'Failed to delete photos');
    }
    setIsEmptyingAll(false);
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error('Failed to sign out');
    } else {
      toast.success('Signed out successfully');
      navigate('/auth');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted p-4">
      <div className="max-w-2xl mx-auto pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Gallery
        </Button>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Email</Label>
                <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
              </div>
              <div>
                <Label>User ID</Label>
                <p className="text-xs text-muted-foreground mt-1 font-mono">{user.id}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum 6 characters
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {organizationId && (
            <Card>
              <CardHeader>
                <CardTitle>Batch Processing</CardTitle>
                <CardDescription>Generate embeddings for existing photos</CardDescription>
              </CardHeader>
              <CardContent>
                <BatchEmbeddingProcessor organizationId={organizationId} />
              </CardContent>
            </Card>
          )}

          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Danger Zone</CardTitle>
              <CardDescription>
                Irreversible actions that permanently delete data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Delete all photos and data from your account
                </p>
                <Button 
                  variant="destructive" 
                  onClick={() => {
                    fetchPhotoCount();
                    setShowEmptyAllDialog(true);
                  }}
                >
                  Empty All Photos
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sign Out</CardTitle>
              <CardDescription>Sign out of your account</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={handleSignOut}>
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>

        <EmptyAllPhotosDialog
          open={showEmptyAllDialog}
          onOpenChange={setShowEmptyAllDialog}
          onConfirm={handleEmptyAllPhotos}
          photoCount={allPhotoCount}
          isLoading={isEmptyingAll}
        />
      </div>
    </div>
  );
}
